import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Container,
} from 'reactstrap';
import moment from 'moment';

import Layout from '../components/Layout';
import EventGroup from '../components/EventGroup';
import EditBookingModal from '../components/EditBookingModal';
import CheckInModal from '../components/CheckInModal';
import api from '../services/api';
import { useReferenceData, useCheckInStates } from '../hooks/useScheduleData';

const SearchEvents = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [includeAll, setIncludeAll] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [criteria, setCriteria] = useState({ query: '', includeAll: false });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetail, setEventDetail] = useState({});
  const [notes, setNotes] = useState('');
  const [editEvent, setEditEvent] = useState(false);

  const { resources: simulators, discountCodes } = useReferenceData();
  const checkInState = useCheckInStates();

  const today = useMemo(() => moment().format('YYYY-MM-DD'), []);

  const isCancelledEvent = useCallback((eventItem) => {
    if (typeof eventItem?.cancelled === 'boolean') {
      return eventItem.cancelled;
    }

    const rawState = eventItem?.state || eventItem?.status || '';
    const normalizedState = String(rawState).toLowerCase();

    return normalizedState === 'cancelled' || normalizedState === 'canceled' || normalizedState.includes('cancel');
  }, []);

  const filteredEvents = useMemo(() => {
    if (showCancelled) {
      return events;
    }

    return events.filter((eventItem) => !isCancelledEvent(eventItem));
  }, [events, isCancelledEvent, showCancelled]);

  useEffect(() => {
    let isMounted = true;

    const fetchEvents = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = {};

        if (!criteria.includeAll) {
          params.start_date = today;
        }

        if (criteria.query) {
          params.query = criteria.query;
          const response = await api.get('/events/search/', { params });

          if (isMounted) {
            setEvents(response.data);
          }
        }

      } catch (err) {
        if (isMounted) {
          setError('Failed to load events.');
        }
        console.error('Failed to search events:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchEvents();

    return () => {
      isMounted = false;
    };
  }, [criteria, today, refreshKey]);

  const triggerRefresh = useCallback(() => {
    setRefreshKey((value) => value + 1);
  }, []);

  const handleSearchSubmit = useCallback(
    (event) => {
      event.preventDefault();
      setCriteria({ query: searchTerm.trim(), includeAll });
    },
    [includeAll, searchTerm]
  );

  const handleIncludeAllChange = useCallback((event) => {
    const { checked } = event.target;
    setIncludeAll(checked);
    setCriteria((prev) => ({ ...prev, includeAll: checked }));
  }, []);

  const handleClear = () => {
    setSearchTerm('');
    setIncludeAll(false);
    setShowCancelled(false);
    setCriteria({ query: '', includeAll: false });
    setEvents([]);
  };

  const toggleModal = useCallback((currentEvent = null) => {
    setModalOpen((open) => !open);
    setSelectedEvent(currentEvent);
    setNotes(currentEvent?.notes || '');
  }, []);

  const handleBookingClick = useCallback(async (id) => {
    try {
      const response = await api.get(`/events/${id}`);
      setEventDetail(response.data);
      setEditEvent(true);
    } catch (err) {
      console.error('Failed to fetch event:', err);
    }
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditEvent(false);
  }, []);

  const handleEditSubmit = useCallback(() => {
    triggerRefresh();
    handleCloseEdit();
  }, [handleCloseEdit, triggerRefresh]);

  const confirmCheckIn = useCallback(
    (status) => {
      if (!selectedEvent) {
        return;
      }

      const payload = {
        check_in_state: status ? status.id : null,
        notes: notes || '',
        state: status === 'noshow' ? 'NoShow' : 'Booked',
      };

      api
        .patch(`/events/${selectedEvent.id}/`, payload)
        .then(() => {
          setModalOpen(false);
          setSelectedEvent(null);
          setNotes('');
          triggerRefresh();
        })
        .catch((err) => console.error('Failed to update check-in:', err));
    },
    [notes, selectedEvent, triggerRefresh]
  );

  const bookingStartISO = useMemo(
    () => (eventDetail?.start ? new Date(eventDetail.start).toISOString() : new Date().toISOString()),
    [eventDetail?.start]
  );

  return (
    <Layout>
      <Container fluid className="d-flex flex-column m-0 p-3 h-100">
        <Row className="m-0 d-flex flex-column">
          <Col className="bg-white border-bottom p-3">
            <Form onSubmit={handleSearchSubmit}>
              <Row className="g-3">
                <Col md="5">
                  <Input
                    id="searchTerm"
                    name="searchTerm"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search upcoming events by name, phone, email, or notes"
                  />
                </Col>

                <Col md="2">
                  <Button color="primary" type="submit" className="w-100">
                    Search
                  </Button>
                </Col>

                <Col md="2" className="d-flex align-items-center">
                  <FormGroup check>
                    <Label
                      check
                      className="fw-semibold"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <Input
                        type="checkbox"
                        checked={includeAll}
                        onChange={handleIncludeAllChange}
                        className="me-2"
                      />
                      Include past events
                    </Label>
                  </FormGroup>
                </Col>

                <Col md="2" className="d-flex align-items-center">
                  <FormGroup check>
                    <Label
                      check
                      className="fw-semibold"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <Input
                        type="checkbox"
                        checked={showCancelled}
                        onChange={(event) => setShowCancelled(event.target.checked)}
                        className="me-2"
                      />
                      Show cancelled events
                    </Label>
                  </FormGroup>
                </Col>

                <Col md="1" className="d-flex align-items-center">
                  <Button
                    color="link"
                    className="p-0 mt-1"
                    onClick={handleClear}>
                    Clear
                  </Button>

                </Col>
              </Row>
            </Form>

          </Col>

          <Col className="p-3">
            {loading && (
              <div className="d-flex justify-content-center py-5">
                <Spinner color="primary" />
              </div>
            )}

            {!loading && error && (
              <Alert color="danger" fade={false}>
                {error}
              </Alert>
            )}

            {!loading && !error && criteria.query && filteredEvents.length === 0 && (
              <Alert color="light" fade={false}>
                No events found.
              </Alert>
            )}

            {!loading && !error && filteredEvents.length > 0 && (
              <EventGroup
                title={`Search Results (${filteredEvents.length})`}
                events={filteredEvents}
                gridSize={4}
                onEdit={handleBookingClick}
                onCheckIn={toggleModal}
                showDate={true}
              />
            )}
          </Col>
        </Row>

        <CheckInModal
          isOpen={modalOpen}
          toggle={() => toggleModal()}
          selectedEvent={selectedEvent}
          checkInState={checkInState}
          notes={notes}
          setNotes={setNotes}
          confirmCheckIn={confirmCheckIn}
        />

        <EditBookingModal
          key={eventDetail?.id || 'new'}
          open={editEvent}
          event={eventDetail}
          bookingDate={bookingStartISO}
          bookingTime={bookingStartISO}
          service={eventDetail?.serviceDetail}
          resources={simulators}
          discountCodes={discountCodes}
          onClose={handleCloseEdit}
          onToday={() => { }}
          onSubmit={handleEditSubmit}
        />
      </Container>
    </Layout>
  );
};

export default SearchEvents;
